import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

// ============================================================
// TYPES
// ============================================================

interface FormatOption {
  id: string;
  name: string;
  price: number;
}

interface CatalogProduct {
  id: string;
  name: string;
  category: string;
  description?: string;
  formats: FormatOption[];
}

interface CartItem {
  product_id: string;
  format_id: string;
  product_name: string;
  format_name: string;
  price: number;
  qty: number;
}

interface StoreSettings {
  accepting_orders: boolean;
  store_name: string;
  store_hours: string;
  pickup_lead_time: number;
  delivery_enabled: boolean;
  min_order_amount: number;
  announcement: string;
}

interface PlacedOrder {
  order_number: string;
  pickup_time: string;
  total: number;
}

type CheckoutStep = "browse" | "checkout" | "confirmation";

// ============================================================
// HELPERS
// ============================================================

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "beer", label: "Beer" },
  { key: "wine", label: "Wine" },
  { key: "liquor", label: "Liquor" },
  { key: "seltzer", label: "Seltzer" },
  { key: "cider", label: "Cider" },
  { key: "na", label: "Non-Alcoholic" },
  { key: "accessory", label: "Accessories" },
];

function generatePickupTimes(leadMinutes: number): string[] {
  const times: string[] = [];
  const now = new Date();
  const start = new Date(now.getTime() + leadMinutes * 60000);
  // Round up to next 15 min
  start.setMinutes(Math.ceil(start.getMinutes() / 15) * 15, 0, 0);
  for (let i = 0; i < 12; i++) {
    const t = new Date(start.getTime() + i * 15 * 60000);
    times.push(t.toISOString());
  }
  return times;
}

// ============================================================
// MAIN PAGE
// ============================================================

export default function CustomerOrderPage() {
  const [settings, setSettings] = useState<StoreSettings | null>(null);
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [category, setCategory] = useState("all");
  const [cart, setCart] = useState<CartItem[]>([]);
  const [step, setStep] = useState<CheckoutStep>("browse");
  const [cartOpen, setCartOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Checkout form
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [pickupTime, setPickupTime] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Loyalty & Gift Card
  const [loyaltyPhone, setLoyaltyPhone] = useState("");
  const [giftCardCode, setGiftCardCode] = useState("");
  const [giftCardBalance, setGiftCardBalance] = useState<number | null>(null);
  const [giftCardApplied, setGiftCardApplied] = useState(0);
  const [giftCardError, setGiftCardError] = useState("");
  const [checkingBalance, setCheckingBalance] = useState(false);

  // Confirmation
  const [placedOrder, setPlacedOrder] = useState<PlacedOrder | null>(null);

  // Fetch settings
  useEffect(() => {
    axios.get("/api/online-orders/settings/public").then((res) => {
      setSettings(res.data);
    }).catch(() => {
      setError("Unable to load store information");
    });
  }, []);

  // Fetch catalog
  const fetchCatalog = useCallback(async () => {
    try {
      setLoading(true);
      const params = category !== "all" ? `?category=${category}` : "";
      const res = await axios.get(`/api/online-orders/catalog${params}`);
      setProducts(res.data);
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  // Cart helpers
  const addToCart = (product: CatalogProduct, format: FormatOption) => {
    setCart((prev) => {
      const existing = prev.find((c) => c.product_id === product.id && c.format_id === format.id);
      if (existing) {
        return prev.map((c) =>
          c.product_id === product.id && c.format_id === format.id
            ? { ...c, qty: c.qty + 1 }
            : c
        );
      }
      return [
        ...prev,
        {
          product_id: product.id,
          format_id: format.id,
          product_name: product.name,
          format_name: format.name,
          price: format.price,
          qty: 1,
        },
      ];
    });
  };

  const updateQty = (productId: string, formatId: string, delta: number) => {
    setCart((prev) =>
      prev
        .map((c) =>
          c.product_id === productId && c.format_id === formatId
            ? { ...c, qty: c.qty + delta }
            : c
        )
        .filter((c) => c.qty > 0)
    );
  };

  const removeItem = (productId: string, formatId: string) => {
    setCart((prev) => prev.filter((c) => !(c.product_id === productId && c.format_id === formatId)));
  };

  const cartTotal = cart.reduce((sum, c) => sum + c.price * c.qty, 0);
  const cartCount = cart.reduce((sum, c) => sum + c.qty, 0);

  // Check gift card balance
  const checkGiftCardBalance = async () => {
    if (!giftCardCode.trim()) return;
    setCheckingBalance(true);
    setGiftCardError("");
    setGiftCardBalance(null);
    setGiftCardApplied(0);
    try {
      const res = await axios.get(`/api/loyalty/gift-cards/balance/${giftCardCode.trim()}`);
      const balance = Number(res.data.balance);
      setGiftCardBalance(balance);
      if (balance > 0) {
        setGiftCardApplied(Math.min(balance, cartTotal));
      }
    } catch {
      setGiftCardError("Invalid gift card code or card not found");
    } finally {
      setCheckingBalance(false);
    }
  };

  const remainingToPay = Math.max(0, cartTotal - giftCardApplied);

  // Place order
  const placeOrder = async () => {
    if (!name.trim() || !phone.trim() || !ageConfirmed || !pickupTime) return;
    if (settings && cartTotal < settings.min_order_amount) return;

    setSubmitting(true);
    setError("");
    try {
      const res = await axios.post("/api/online-orders/place", {
        customer_name: name.trim(),
        customer_phone: phone.trim(),
        age_confirmed: ageConfirmed,
        order_type: "pickup",
        pickup_time: pickupTime,
        loyalty_phone: loyaltyPhone.trim() || undefined,
        gift_card_code: giftCardApplied > 0 ? giftCardCode.trim() : undefined,
        gift_card_amount: giftCardApplied > 0 ? giftCardApplied : undefined,
        items: cart.map((c) => ({
          product_id: c.product_id,
          format_id: c.format_id,
          qty: c.qty,
        })),
      });
      setPlacedOrder(res.data);
      setStep("confirmation");
      setCart([]);
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError("Failed to place order. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const pickupTimes = settings ? generatePickupTimes(settings.pickup_lead_time) : [];

  // ============================================================
  // CONFIRMATION VIEW
  // ============================================================
  if (step === "confirmation" && placedOrder) {
    return (
      <div className="min-h-screen" style={{ background: "var(--rbw-bg)" }}>
        <Header storeName={settings?.store_name} />
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ background: "#2D8A4E18", border: "2px solid var(--rbw-success)" }}
          >
            <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="var(--rbw-success)" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: "var(--rbw-navy)" }}>
            Order Placed!
          </h2>
          <div
            className="inline-block px-5 py-2 rounded-xl text-xl font-bold mb-4"
            style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
          >
            Order #{placedOrder.order_number}
          </div>
          <p className="text-sm mb-2" style={{ color: "var(--rbw-gray-dark)" }}>
            Pickup at {new Date(placedOrder.pickup_time).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
          </p>
          <p className="text-lg font-semibold mb-8" style={{ color: "var(--rbw-navy)" }}>
            Total: ${Number(placedOrder.total).toFixed(2)}
          </p>
          <p className="text-sm mb-6" style={{ color: "var(--rbw-gray-dark)" }}>
            You will receive a text when your order is ready.
          </p>
          <button
            onClick={() => {
              setStep("browse");
              setPlacedOrder(null);
              setName("");
              setPhone("");
              setAgeConfirmed(false);
              setPickupTime("");
            }}
            className="px-6 py-3 rounded-xl font-semibold transition-all"
            style={{ background: "var(--rbw-navy)", color: "#fff" }}
          >
            Place Another Order
          </button>
        </div>
      </div>
    );
  }

  // ============================================================
  // NOT ACCEPTING / LOADING
  // ============================================================
  if (settings && !settings.accepting_orders) {
    return (
      <div className="min-h-screen" style={{ background: "var(--rbw-bg)" }}>
        <Header storeName={settings.store_name} />
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <div className="text-5xl mb-4 opacity-30">&#128683;</div>
          <h2 className="text-xl font-bold mb-2" style={{ color: "var(--rbw-navy)" }}>
            Online Ordering is Currently Unavailable
          </h2>
          <p className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>
            Please check back later or visit us in store.
          </p>
          {settings.announcement && (
            <div
              className="mt-6 px-5 py-3 rounded-xl inline-block"
              style={{ background: "#F6BE0015", border: "1px solid #F6BE0040", color: "var(--rbw-gold-dark)" }}
            >
              {settings.announcement}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============================================================
  // CHECKOUT VIEW
  // ============================================================
  if (step === "checkout") {
    const canSubmit = name.trim() && phone.trim() && ageConfirmed && pickupTime && cart.length > 0 &&
      (!settings || cartTotal >= settings.min_order_amount);

    return (
      <div className="min-h-screen" style={{ background: "var(--rbw-bg)" }}>
        <Header storeName={settings?.store_name} />
        <div className="max-w-lg mx-auto px-4 py-8">
          <button
            onClick={() => setStep("browse")}
            className="flex items-center gap-1.5 text-sm font-medium mb-6 transition-colors"
            style={{ color: "var(--rbw-gray-dark)" }}
          >
            &larr; Back to menu
          </button>

          <h2 className="text-2xl font-bold mb-6" style={{ color: "var(--rbw-navy)" }}>
            Checkout
          </h2>

          {error && (
            <div
              className="rounded-xl p-4 mb-4 text-sm"
              style={{ background: "#C5303010", border: "1px solid #C5303035", color: "var(--rbw-danger)" }}
            >
              {error}
            </div>
          )}

          {/* Order summary */}
          <div
            className="rounded-2xl p-5 mb-6"
            style={{ background: "var(--rbw-bg-card)", border: "1px solid var(--rbw-border)" }}
          >
            <h3 className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: "var(--rbw-gray-dark)" }}>
              Your Order
            </h3>
            {cart.map((item) => (
              <div
                key={`${item.product_id}-${item.format_id}`}
                className="flex justify-between items-center py-2 text-sm"
                style={{ borderBottom: "1px solid var(--rbw-border)" }}
              >
                <div>
                  <span className="font-semibold" style={{ color: "var(--rbw-navy)" }}>
                    {item.qty}x {item.product_name}
                  </span>
                  <span className="ml-1" style={{ color: "var(--rbw-gray-dark)" }}>
                    - {item.format_name}
                  </span>
                </div>
                <span className="font-medium" style={{ color: "var(--rbw-navy)" }}>
                  ${(item.price * item.qty).toFixed(2)}
                </span>
              </div>
            ))}
            <div className="flex justify-between mt-3 font-bold" style={{ color: "var(--rbw-navy)" }}>
              <span>Total</span>
              <span>${cartTotal.toFixed(2)}</span>
            </div>
          </div>

          {/* Customer info form */}
          <div className="flex flex-col gap-4">
            <div>
              <label className="block text-sm font-semibold mb-1.5" style={{ color: "var(--rbw-navy)" }}>
                Your Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                required
                className="w-full px-4 py-3 rounded-xl outline-none transition-all text-base"
                style={{ border: "2px solid var(--rbw-border)", background: "var(--rbw-bg-card)", color: "var(--rbw-navy)" }}
                onFocus={(e) => (e.target.style.borderColor = "var(--rbw-blue)")}
                onBlur={(e) => (e.target.style.borderColor = "var(--rbw-border)")}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1.5" style={{ color: "var(--rbw-navy)" }}>
                Phone Number
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 555-5555"
                required
                className="w-full px-4 py-3 rounded-xl outline-none transition-all text-base"
                style={{ border: "2px solid var(--rbw-border)", background: "var(--rbw-bg-card)", color: "var(--rbw-navy)" }}
                onFocus={(e) => (e.target.style.borderColor = "var(--rbw-blue)")}
                onBlur={(e) => (e.target.style.borderColor = "var(--rbw-border)")}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1.5" style={{ color: "var(--rbw-navy)" }}>
                Pickup Time
              </label>
              <select
                value={pickupTime}
                onChange={(e) => setPickupTime(e.target.value)}
                className="w-full px-4 py-3 rounded-xl outline-none transition-all text-base"
                style={{ border: "2px solid var(--rbw-border)", background: "var(--rbw-bg-card)", color: "var(--rbw-navy)" }}
                onFocus={(e) => (e.target.style.borderColor = "var(--rbw-blue)")}
                onBlur={(e) => (e.target.style.borderColor = "var(--rbw-border)")}
              >
                <option value="">Select a time...</option>
                {pickupTimes.map((t) => (
                  <option key={t} value={t}>
                    {new Date(t).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                  </option>
                ))}
              </select>
            </div>

            {/* Loyalty Phone */}
            <div
              className="rounded-2xl p-4"
              style={{ background: "var(--rbw-bg)", border: "1px solid var(--rbw-border)" }}
            >
              <label className="block text-sm font-semibold mb-1.5" style={{ color: "var(--rbw-navy)" }}>
                Loyalty Number (optional)
              </label>
              <p className="text-xs mb-2" style={{ color: "var(--rbw-gray-dark)" }}>
                Enter your loyalty number to earn points on this order
              </p>
              <input
                type="tel"
                value={loyaltyPhone}
                onChange={(e) => setLoyaltyPhone(e.target.value)}
                placeholder="(555) 555-5555"
                className="w-full px-4 py-3 rounded-xl outline-none transition-all text-base"
                style={{ border: "2px solid var(--rbw-border)", background: "var(--rbw-bg-card)", color: "var(--rbw-navy)" }}
                onFocus={(e) => (e.target.style.borderColor = "var(--rbw-blue)")}
                onBlur={(e) => (e.target.style.borderColor = "var(--rbw-border)")}
              />
            </div>

            {/* Gift Card Payment */}
            <div
              className="rounded-2xl p-4"
              style={{ background: "var(--rbw-bg)", border: "1px solid var(--rbw-border)" }}
            >
              <label className="block text-sm font-semibold mb-1.5" style={{ color: "var(--rbw-navy)" }}>
                Gift Card
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={giftCardCode}
                  onChange={(e) => {
                    setGiftCardCode(e.target.value);
                    setGiftCardBalance(null);
                    setGiftCardApplied(0);
                    setGiftCardError("");
                  }}
                  placeholder="Enter gift card code"
                  className="flex-1 px-4 py-3 rounded-xl outline-none transition-all text-base"
                  style={{ border: "2px solid var(--rbw-border)", background: "var(--rbw-bg-card)", color: "var(--rbw-navy)" }}
                  onFocus={(e) => (e.target.style.borderColor = "var(--rbw-blue)")}
                  onBlur={(e) => (e.target.style.borderColor = "var(--rbw-border)")}
                />
                <button
                  onClick={checkGiftCardBalance}
                  disabled={!giftCardCode.trim() || checkingBalance}
                  className="px-4 py-3 rounded-xl font-semibold text-sm transition-all disabled:opacity-40"
                  style={{ background: "var(--rbw-navy)", color: "#fff" }}
                >
                  {checkingBalance ? "..." : "Check Balance"}
                </button>
              </div>
              {giftCardError && (
                <p className="text-xs mt-2" style={{ color: "var(--rbw-danger)" }}>{giftCardError}</p>
              )}
              {giftCardBalance !== null && (
                <div className="mt-2 p-3 rounded-xl" style={{ background: "#2D8A4E10", border: "1px solid #2D8A4E30" }}>
                  <div className="flex justify-between text-sm">
                    <span style={{ color: "var(--rbw-gray-dark)" }}>Card Balance</span>
                    <span className="font-bold" style={{ color: "var(--rbw-success)" }}>${giftCardBalance.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span style={{ color: "var(--rbw-gray-dark)" }}>Applied to Order</span>
                    <span className="font-bold" style={{ color: "var(--rbw-navy)" }}>-${giftCardApplied.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1 pt-2" style={{ borderTop: "1px solid #2D8A4E30" }}>
                    <span className="font-semibold" style={{ color: "var(--rbw-navy)" }}>Remaining to Pay</span>
                    <span className="font-bold" style={{ color: "var(--rbw-navy)" }}>${remainingToPay.toFixed(2)}</span>
                  </div>
                </div>
              )}
              <a
                href="/gift-cards"
                className="inline-block text-xs mt-2 transition-colors"
                style={{ color: "var(--rbw-blue-dark)" }}
              >
                Gift cards make great gifts! Buy one here
              </a>
            </div>

            {/* Age confirmation */}
            <label className="flex items-start gap-3 cursor-pointer mt-2">
              <input
                type="checkbox"
                checked={ageConfirmed}
                onChange={(e) => setAgeConfirmed(e.target.checked)}
                className="mt-0.5 w-5 h-5 rounded"
                style={{ accentColor: "var(--rbw-navy)" }}
              />
              <span className="text-sm font-medium" style={{ color: "var(--rbw-navy)" }}>
                I confirm that I am 21 years of age or older. A valid ID will be required at pickup.
              </span>
            </label>

            {settings && cartTotal < settings.min_order_amount && (
              <div className="text-sm" style={{ color: "var(--rbw-danger)" }}>
                Minimum order amount is ${settings.min_order_amount.toFixed(2)}
              </div>
            )}

            <button
              onClick={placeOrder}
              disabled={!canSubmit || submitting}
              className="w-full py-4 rounded-xl font-bold text-lg transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed mt-2"
              style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
            >
              {submitting ? "Placing Order..." : `Place Order - $${giftCardApplied > 0 ? remainingToPay.toFixed(2) : cartTotal.toFixed(2)}`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // BROWSE VIEW (default)
  // ============================================================
  return (
    <div className="min-h-screen" style={{ background: "var(--rbw-bg)" }}>
      <Header storeName={settings?.store_name} storeHours={settings?.store_hours} announcement={settings?.announcement} />

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Category filter */}
        <div className="flex gap-2 overflow-x-auto pb-3 mb-6 -mx-4 px-4" style={{ scrollbarWidth: "none" }}>
          {CATEGORIES.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setCategory(cat.key)}
              className="px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all flex-shrink-0"
              style={
                category === cat.key
                  ? { background: "var(--rbw-navy)", color: "#fff" }
                  : { background: "var(--rbw-bg-card)", color: "var(--rbw-navy)", border: "1px solid var(--rbw-border)" }
              }
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Product grid */}
        {loading ? (
          <div className="text-center py-16">
            <div className="text-lg" style={{ color: "var(--rbw-gray-dark)" }}>Loading products...</div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-5xl mb-4 opacity-30">&#127866;</div>
            <div className="text-lg font-medium" style={{ color: "var(--rbw-gray-dark)" }}>
              No products in this category
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} onAdd={addToCart} />
            ))}
          </div>
        )}
      </div>

      {/* Events & Catering Promo */}
      <div className="max-w-6xl mx-auto px-4 pb-6">
        <div
          className="rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          style={{ background: "#7C3AED10", border: "1px solid #7C3AED30" }}
        >
          <div className="flex-1">
            <h3 className="text-base font-bold mb-1" style={{ color: "var(--rbw-navy)" }}>
              Planning a wedding or event?
            </h3>
            <p className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>
              We offer volume discounts and free delivery on orders over $500. Contact us or request a quote.
            </p>
          </div>
          <a
            href="/event-inquiry"
            className="px-5 py-2.5 rounded-xl font-semibold text-sm transition-all whitespace-nowrap"
            style={{ background: "var(--rbw-navy)", color: "#fff" }}
          >
            Request a Quote
          </a>
        </div>
      </div>

      {/* Cart bar (fixed bottom on mobile / sidebar on desktop) */}
      {cart.length > 0 && (
        <>
          {/* Mobile bottom bar */}
          <div className="fixed bottom-0 left-0 right-0 lg:hidden z-40">
            <button
              onClick={() => setCartOpen(!cartOpen)}
              className="w-full py-4 px-6 flex items-center justify-between font-bold text-base"
              style={{ background: "var(--rbw-navy)", color: "#fff" }}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" />
                </svg>
                {cartCount} item{cartCount !== 1 ? "s" : ""}
              </span>
              <span>${cartTotal.toFixed(2)}</span>
            </button>

            {/* Expanded cart */}
            {cartOpen && (
              <div
                className="max-h-[60vh] overflow-y-auto p-4"
                style={{ background: "var(--rbw-bg-card)", borderTop: "1px solid var(--rbw-border)" }}
              >
                <CartContents
                  cart={cart}
                  onUpdateQty={updateQty}
                  onRemove={removeItem}
                  total={cartTotal}
                />
                <button
                  onClick={() => { setStep("checkout"); setCartOpen(false); }}
                  className="w-full py-3 rounded-xl font-bold text-base mt-4 transition-all active:scale-[0.98]"
                  style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
                >
                  Checkout - ${cartTotal.toFixed(2)}
                </button>
              </div>
            )}
          </div>

          {/* Desktop sidebar */}
          <div
            className="hidden lg:block fixed top-0 right-0 w-80 h-screen overflow-y-auto z-30"
            style={{
              background: "var(--rbw-bg-card)",
              borderLeft: "1px solid var(--rbw-border)",
              boxShadow: "-4px 0 12px rgba(51,63,72,0.06)",
            }}
          >
            <div className="p-5">
              <h3 className="text-lg font-bold mb-4" style={{ color: "var(--rbw-navy)" }}>
                Your Cart ({cartCount})
              </h3>
              <CartContents
                cart={cart}
                onUpdateQty={updateQty}
                onRemove={removeItem}
                total={cartTotal}
              />
              <button
                onClick={() => setStep("checkout")}
                className="w-full py-3 rounded-xl font-bold text-base mt-4 transition-all active:scale-[0.98]"
                style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
              >
                Checkout - ${cartTotal.toFixed(2)}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Spacer for fixed bottom bar on mobile */}
      {cart.length > 0 && <div className="h-16 lg:hidden" />}
    </div>
  );
}

// ============================================================
// SUB-COMPONENTS
// ============================================================

function Header({
  storeName,
  storeHours,
  announcement,
}: {
  storeName?: string;
  storeHours?: string;
  announcement?: string;
}) {
  return (
    <header style={{ background: "var(--rbw-navy)" }}>
      <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4">
        <img
          src="/images/logo-secondary.jpg"
          alt="Rieker Bottle Works"
          className="h-10 rounded"
        />
        <div>
          <h1 className="text-xl font-bold" style={{ color: "#fff" }}>
            {storeName || "Rieker Bottle Works"}
          </h1>
          {storeHours && (
            <p className="text-xs" style={{ color: "var(--rbw-gray)" }}>
              {storeHours}
            </p>
          )}
        </div>
      </div>
      {announcement && (
        <div
          className="text-center text-sm py-2 font-medium"
          style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
        >
          {announcement}
        </div>
      )}
    </header>
  );
}

function ProductCard({
  product,
  onAdd,
}: {
  product: CatalogProduct;
  onAdd: (product: CatalogProduct, format: FormatOption) => void;
}) {
  return (
    <div
      className="rounded-2xl p-5 flex flex-col"
      style={{
        background: "var(--rbw-bg-card)",
        border: "1px solid var(--rbw-border)",
        boxShadow: "0 1px 3px rgba(51,63,72,0.04)",
      }}
    >
      <h3 className="text-base font-bold mb-1" style={{ color: "var(--rbw-navy)" }}>
        {product.name}
      </h3>
      <span
        className="inline-block text-xs px-2 py-0.5 rounded-full font-medium mb-3 self-start"
        style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-gray-dark)" }}
      >
        {product.category}
      </span>
      {product.description && (
        <p className="text-sm mb-3" style={{ color: "var(--rbw-gray-dark)" }}>
          {product.description}
        </p>
      )}
      <div className="flex flex-col gap-2 mt-auto">
        {product.formats.map((fmt) => (
          <button
            key={fmt.id}
            onClick={() => onAdd(product, fmt)}
            className="flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all"
            style={{
              background: "var(--rbw-bg)",
              border: "1px solid var(--rbw-border)",
              color: "var(--rbw-navy)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "var(--rbw-gold)";
              e.currentTarget.style.background = "#F6BE0010";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "var(--rbw-border)";
              e.currentTarget.style.background = "var(--rbw-bg)";
            }}
          >
            <span className="font-medium">{fmt.name}</span>
            <span className="flex items-center gap-2">
              <span className="font-bold">${Number(fmt.price).toFixed(2)}</span>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function CartContents({
  cart,
  onUpdateQty,
  onRemove,
  total,
}: {
  cart: CartItem[];
  onUpdateQty: (productId: string, formatId: string, delta: number) => void;
  onRemove: (productId: string, formatId: string) => void;
  total: number;
}) {
  return (
    <div className="flex flex-col gap-3">
      {cart.map((item) => (
        <div
          key={`${item.product_id}-${item.format_id}`}
          className="flex items-center gap-3 pb-3"
          style={{ borderBottom: "1px solid var(--rbw-border)" }}
        >
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate" style={{ color: "var(--rbw-navy)" }}>
              {item.product_name}
            </div>
            <div className="text-xs" style={{ color: "var(--rbw-gray-dark)" }}>
              {item.format_name} - ${Number(item.price).toFixed(2)} ea.
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              onClick={() => onUpdateQty(item.product_id, item.format_id, -1)}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-sm font-bold transition-colors"
              style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-navy)" }}
            >
              -
            </button>
            <span className="w-6 text-center text-sm font-bold" style={{ color: "var(--rbw-navy)" }}>
              {item.qty}
            </span>
            <button
              onClick={() => onUpdateQty(item.product_id, item.format_id, 1)}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-sm font-bold transition-colors"
              style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-navy)" }}
            >
              +
            </button>
          </div>
          <div className="text-right flex-shrink-0 w-16">
            <div className="text-sm font-bold" style={{ color: "var(--rbw-navy)" }}>
              ${(item.price * item.qty).toFixed(2)}
            </div>
            <button
              onClick={() => onRemove(item.product_id, item.format_id)}
              className="text-xs transition-colors"
              style={{ color: "var(--rbw-danger)" }}
            >
              Remove
            </button>
          </div>
        </div>
      ))}
      <div className="flex justify-between font-bold pt-1" style={{ color: "var(--rbw-navy)" }}>
        <span>Total</span>
        <span>${total.toFixed(2)}</span>
      </div>
    </div>
  );
}
